import { useEffect, useMemo, useState } from "react";

import { getRequestsListByPortal } from "../utils/requestApis";
import {
  getSupervisorOptionsFromEventZones,
  mapEventRequestsToSupervisorOptions,
  mergeSupervisorOptions,
} from "../utils/supervisorOptions";

const SUPERVISOR_REQUEST_PARAMS = {
  is_supervisor: true,
  order_by_created_at: "asc",
  status: "approved",
  is_assigned_to_zone: false,
};

const buildSupervisorRequestsParams = (eventId, requestsPortal) => {
  const base = {
    ...SUPERVISOR_REQUEST_PARAMS,
    event_id: eventId,
  };

  if (requestsPortal === "company") {
    return { ...base, page: 1, items: 1000 };
  }

  return { ...base, page: -1 };
};

/**
 * Approved supervisors for zone assignment — same source as legacy StepThree.
 * Merges API list with supervisors already saved on event zones.
 */
const useEventSupervisorOptions = ({
  eventId,
  requestsPortal = "admin",
  initialEvent,
  enabled = true,
  extraOptions = [],
  lang = "en",
  refreshKey,
}) => {
  const [requests, setRequests] = useState([]);

  useEffect(() => {
    if (!enabled || !eventId) {
      setRequests([]);
      return undefined;
    }

    let cancelled = false;

    const loadSupervisors = async () => {
      try {
        const getRequestsList = getRequestsListByPortal(requestsPortal);
        const response = await getRequestsList(
          buildSupervisorRequestsParams(eventId, requestsPortal),
        );
        if (cancelled) return;
        setRequests(response?.data?.data || []);
      } catch (_error) {
        if (!cancelled) setRequests([]);
      }
    };

    void loadSupervisors();

    return () => {
      cancelled = true;
    };
    // `refreshKey` re-runs the fetch after a zone's supervisor changes: a freed
    // supervisor only comes back through this list, never through event zones.
  }, [enabled, eventId, requestsPortal, refreshKey]);

  return useMemo(
    () =>
      mergeSupervisorOptions(
        mapEventRequestsToSupervisorOptions(requests, lang),
        getSupervisorOptionsFromEventZones(initialEvent, lang),
        extraOptions,
      ),
    [requests, initialEvent, extraOptions, lang],
  );
};

export default useEventSupervisorOptions;
